// pages/Admin/Users/UsersTable.jsx
import React from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    useMediaQuery,
    IconButton,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { safeDateFormat } from "qubCommon/helperMethods/dateHelper";
import ScrollSyncContainer from "qubCommon/ScrollSyncContainer";
import { Refresh } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { resendInvitation } from "apis/controllers/users";

const UsersTable = ({
    rows = [],
    onSelect,
    sortBy,
    sortDirection,
    onSortChange,
}) => {
    const theme = useTheme();
    const [resending, setResending] = React.useState(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const renderSortableHeader = (field, label) => {
        const active = sortBy === field;
        const direction = active ? sortDirection : "asc";

        return (
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.3,
                    cursor: "pointer",
                    userSelect: "none",
                }}
                onClick={() => onSortChange?.(field)}
            >
                <Typography
                    fontWeight={600}
                    sx={{
                        color: active ? "primary.main" : "text.primary",
                        lineHeight: 1.2, // ⭐ keeps text aligned
                    }}
                >
                    {label}
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                    }}
                >
                    <ArrowDropUpIcon
                        sx={{
                            fontSize: 18,
                            mt: "-4px",
                            color:
                                active && direction === "asc"
                                    ? "primary.main"
                                    : "text.disabled",
                        }}
                    />
                    <ArrowDropDownIcon
                        sx={{
                            fontSize: 18,
                            mt: "-10px",
                            color:
                                active && direction === "desc"
                                    ? "primary.main"
                                    : "text.disabled",
                        }}
                    />
                </Box>
            </Box>

        );
    };

    const { enqueueSnackbar } = useSnackbar();

    const handleResendInvitation = async (id) => {
        if (!!resending) return;

        setResending(id);
        try {
            const res = await resendInvitation(id);

            if (res?.status === "success") {
                enqueueSnackbar("Η πρόσκληση επαναστάλθηκε με επιτυχία.", {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(
                    res?.message ||
                    "Παρουσιάστηκε σφάλμα κατά την επαναποστολή της πρόσκλησης.",
                    { variant: "error" }
                );
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar(
                "Παρουσιάστηκε σφάλμα κατά την επαναποστολή της πρόσκλησης.",
                { variant: "error" }
            );
        } finally {
            setResending(null);
        }
    };


    if (!rows || !rows.length) return null;

    // =====================================
    // 📱 MOBILE (Card Layout)
    // =====================================
    // 📱 MOBILE (Card Layout)
    // =====================================
    if (isMobile) {
        return (
            <Box sx={{ mt: 2 }}>
                {rows.map((row, idx) => (
                    <Paper
                        key={row.id || idx}
                        elevation={0}
                        sx={{
                            position: "relative",
                            border: "1px solid #dcdcdc",
                            borderRadius: 2,
                            mb: 1.5,
                            p: 2,
                            backgroundColor: "#fff",
                            pb: 4
                        }}
                    >
                        {/* Ονοματεπώνυμο */}
                        <Typography color="primary" fontWeight={700} sx={{ mb: 0.5 }}>
                            {row.lastName} {row?.firstName}
                        </Typography>

                        {/* Email */}
                        <Typography variant="body2" color="text.secondary">
                            Email:{" "}
                            <Typography component="span" color="primary.main">
                                {row.email}
                            </Typography>
                        </Typography>



                        {/* Φύλο */}
                        <Typography variant="body2" color="text.secondary">
                            Φύλο:{" "}
                            <Typography component="span" color="text.primary">
                                {row.gender.name || "-"}
                            </Typography>
                        </Typography>

                        {/* Ρόλοι */}
                        <Box >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                            >
                                Ρόλοι:
                            </Typography>

                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {row.roles?.length ? (
                                    row.roles.map((r, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                px: 1.2,
                                                py: "3px",
                                                fontSize: "0.75rem",
                                                fontWeight: 600,
                                                borderRadius: "12px",
                                                backgroundColor: "#f0f0f0",
                                                color: "#0d47a1",
                                            }}
                                        >
                                            {typeof r === "string" ? r : r.name}
                                        </Box>
                                    ))
                                ) : (
                                    <Typography variant="body2" color="text.disabled">
                                        -
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        {/* Κατάσταση */}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            Κατάσταση:
                            <Box
                                component="span"
                                sx={{
                                    ml: 1,
                                    backgroundColor:
                                        row.status?.name === "Ενεργός"
                                            ? "rgba(76, 175, 80, 0.15)"
                                            : "rgba(244, 67, 54, 0.15)",
                                    color:
                                        row.status?.name === "Ενεργός"
                                            ? "#2e7d32"
                                            : "#c62828",
                                    borderRadius: "12px",
                                    px: 1.2,
                                    py: "2px",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                }}
                            >
                                {row.status?.name || "-"}
                            </Box>
                        </Typography>

                        {/* Floating action buttons */}
                        <Box
                            sx={{
                                position: "absolute",
                                right: 10,
                                bottom: 10,
                                display: "flex",
                                gap: 1.2,
                            }}
                        >
                            {row?.canEdit && (
                                <Tooltip title="Επεξεργασία">
                                    <IconButton
                                        color="primary"
                                        onClick={() => onSelect?.(row)}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            backgroundColor: "primary.main",
                                            color: "#fff",
                                            "&:hover": { backgroundColor: "primary.dark" },
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.20)",
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {row?.canResendInvite && (
                                <Tooltip title="Επαναποστολή">
                                    <IconButton
                                        onClick={() => handleResendInvitation(row?.id)}
                                        disabled={resending === row?.id}
                                        startIcon={resending === row?.id ? <CircularProgress size={20} /> : null}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: "50%",
                                            backgroundColor: "warning.dark",
                                            color: "#fff",
                                            "&:hover": { backgroundColor: "rgba(255, 136, 1, 0.93)" },
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.20)",
                                        }}
                                    >
                                        <Refresh sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>

                    </Paper>
                ))}
            </Box>
        );
    }


    // =====================================
    // 🖥 DESKTOP TABLE
    // =====================================
    return (
        <ScrollSyncContainer>

            <Box sx={{
                minWidth: "1100px", border: "1px solid #dcdcdc",
                borderRadius: 2,
                overflowX: "auto",
                mt: 2,
            }}>
                {/* HEADER */}
                <Grid
                    container
                    sx={{
                        backgroundColor: "#f5f5f5",
                        borderBottom: "1px solid #dcdcdc",
                        py: 1.2,
                        px: 2,
                        alignItems: "center", // ⭐
                    }}
                >
                    <Grid item xs={2.5}>
                        {renderSortableHeader("fullName", "Ονοματεπώνυμο")}
                    </Grid>
                    <Grid item xs={3.5}>
                        {renderSortableHeader("email", "Email")}
                    </Grid>

                    <Grid item xs={1}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                height: "20px",   // ⭐ ίδιο ύψος με τα sortable
                            }}
                        >
                            <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                Φύλο
                            </Typography>
                        </Box>

                    </Grid>
                    <Grid item xs={2.5}>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                height: "20px",   // ⭐ ίδιο ύψος με τα sortable
                            }}
                        >
                            <Typography fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                Ρόλοι
                            </Typography>
                        </Box>

                    </Grid>
                    <Grid item xs={1.5}>
                        {renderSortableHeader("status", "Κατάσταση")}
                    </Grid>
                    <Grid item xs={1} textAlign="center">
                        <Typography fontWeight={600}>Ενέργεια</Typography>
                    </Grid>
                </Grid>


                {/* Rows */}
                {rows.map((row, idx) => (
                    <Grid
                        key={row.id}
                        container
                        sx={{
                            backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                            borderBottom:
                                idx < rows.length - 1 ? "1px solid #eee" : "none",
                            py: 1.2,
                            px: 2,
                            alignItems: "center",
                        }}
                    >
                        <Grid item xs={2.5}>
                            <Typography color="primary" fontWeight={600}>{row.lastName} {row?.firstName}</Typography>
                        </Grid>

                        <Grid item xs={3.5}>
                            <Typography>{row.email}</Typography>
                        </Grid>



                        <Grid item xs={1} >
                            <Typography>{row.gender.name}</Typography>
                        </Grid>

                        <Grid item xs={2.5}>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                {row.roles?.map((r, i) => (
                                    <Typography key={i} sx={{ lineHeight: 1.2 }}>
                                        • {typeof r === "string" ? r : r.name}
                                    </Typography>
                                ))}
                            </Box>
                        </Grid>

                        <Grid item xs={1.5}>
                            <Box
                                sx={{
                                    backgroundColor: row.status?.name === "Ενεργός"
                                        ? "rgba(56,142,60,0.15)"
                                        : "rgba(244,67,54,0.15)",
                                    color: row.status?.name === "Ενεργός" ? "#2e7d32" : "#c62828",
                                    borderRadius: "10px",
                                    px: 1.2,
                                    py: "3px",
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    width: "fit-content",
                                }}
                            >
                                {row.status?.name}
                            </Box>
                        </Grid>

                        <Grid
                            container
                            item
                            xs={1}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                py: 0
                            }}
                            spacing={1}
                        >
                            <Grid item>
                                {row?.canEdit && (
                                    <Tooltip title='Επεξεργασία'>
                                        <IconButton
                                            onClick={() => onSelect(row)}
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: "6px", // << square
                                                backgroundColor: "secondary.main",
                                                color: "#fff",
                                                "&:hover": { backgroundColor: "secondary.dark" },
                                            }}
                                        >
                                            <EditIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Tooltip>)}
                            </Grid>
                            <Grid item>
                                {row?.canResendInvite && (<Tooltip title='Επαναποστολή'>
                                    <IconButton
                                        onClick={() => handleResendInvitation(row?.id)}
                                        disabled={row?.id === resending}
                                        startIcon={row?.id === resending ? <CircularProgress size={20} /> : null}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: "6px", // << square
                                            backgroundColor: "warning.dark",
                                            color: "#fff",
                                            "&:hover": { backgroundColor: "#ffa600ff" },
                                        }}
                                    >
                                        <Refresh sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Tooltip>)}
                            </Grid>
                        </Grid>
                    </Grid>

                ))}
            </Box>
        </ScrollSyncContainer >
    );
};

export default UsersTable;
